/**
 * <s-mobile-menu> — full-screen mobile navigation panel that opens below the sticky header.
 * Toggled by a button with commandfor="<id>" command="--toggle"; the panel measures the
 * header's bottom edge on open so the header (and its close icon) stays visible.
 * Closes on Escape, on link activation and when the viewport reaches desktop width.
 */
class SMobileMenu extends HTMLElement {
  #ac;
  #source = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command !== '--toggle') return;
      this.#source = event.source instanceof HTMLElement ? event.source : null;
      this.hasAttribute('open') ? this.close() : this.open();
    }, opts);

    this.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.close();
    }, opts);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.hasAttribute('open')) {
        this.close();
        this.#source?.focus();
      }
    }, opts);

    matchMedia('(min-width: 64rem)').addEventListener('change', (event) => {
      if (event.matches) this.close();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    document.documentElement.classList.remove('overflow-hidden');
  }

  open() {
    const header = this.closest('header');
    const top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    this.style.setProperty('--menu-top', `${top}px`);
    this.setAttribute('open', '');
    this.#source?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('overflow-hidden');
  }

  close() {
    this.removeAttribute('open');
    this.#source?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('overflow-hidden');
  }
}

if (!customElements.get('s-mobile-menu')) {
  customElements.define('s-mobile-menu', SMobileMenu);
}
